import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
	DataSourceLoadOptions,
	RefundExecutionArgs,
	ReplacementDispatchArgs,
	ReplacementLineDispatchArgs,
	ReplacementSelectionArgs,
	ReturnBulkStatusOptionToken,
	ReturnItemsUpdateArgs,
	ReturnItemUpdateArgs,
	ReturnPolicyToken,
	ReturnRequestDetailToken,
	ReturnSettingToken,
	ReturnSettingUpdateArgs
} from '@sleed/bizcommerce-api';
import type { ReturnPolicyEntityType } from '@sleed/bizcommerce-api';
import { returnPolicyApi, returnSettingsApi, returnsApi, settingsApi } from '~/lib/api/client';
import { applySort, type GridSort } from '~/lib/grid/sort';
import { buildReturnsFilter, type ReturnsFilters } from '~/lib/returns/grid-filter';
import { toGridRow, type ReturnGridRow } from '~/lib/returns/grid-row';

/** `Returns.Enabled`, the per-site switch that decides whether the module exists at all. */
const RETURNS_ENABLED_SETTING = 'Returns.Enabled';

/** Upper bound on an export. The queue is a work list, not an archive — see `fetchReturnsForExport`. */
const EXPORT_LIMIT = 5000;

export const returnKeys = {
	all: ['returns'] as const,
	enabled: () => ['returns', 'enabled'] as const,
	list: (params: unknown) => ['returns', 'list', params] as const,
	detail: (requestId: number) => ['returns', 'detail', requestId] as const,
	policy: (entityType: ReturnPolicyEntityType, entityId: number) => ['returns', 'policy', entityType, entityId] as const,
	variants: (productId: number) => ['returns', 'replacement-variants', productId] as const,
	settings: () => ['returns', 'settings'] as const,
	bulkStatusOptions: (itemIds: readonly number[]) => ['returns', 'bulk-status-options', itemIds] as const
};

/** One selectable replacement variant. */
export interface ReplacementVariant {
	id: number;
	label: string;
}

/**
 * The variants of whichever product a replacement currently targets — only the ones that can actually be
 * sent: visible, in stock and priced.
 *
 * Read from the module's own `returns/replacement-variants`, not `ProductDimensionsApi.getByProduct`. Two
 * reasons, the same two admin-v1 already acted on: that endpoint is `[Authorize(Roles = "Catalog")]` while
 * this page is `Orders`-role, so an orders administrator got a 403 and an empty list; and it returns *every*
 * variant, so the administrator could pick a hidden or exhausted one and only learn on save. The module
 * endpoint runs the same rule as the customer's lookup and the save. An e-shop with no variants at all
 * simply gets an empty list (Δ-04).
 */
export function useReplacementVariants(productId: number | undefined) {
	return useQuery<ReplacementVariant[]>({
		queryKey: returnKeys.variants(productId ?? 0),
		enabled: Boolean(productId),
		staleTime: 5 * 60_000,
		queryFn: async () => {
			const res = await returnsApi.replacementVariants(productId);

			// `dimensionId` is optional on the generated token, and an id-less entry would render
			// `value="undefined"` → `Number('undefined')` → NaN → serialised as null, i.e. a silent
			// "no variant" on save. Drop it instead.
			return (res.data.items ?? [])
				.filter(v => v.dimensionId != null)
				.map(v => ({
					id: v.dimensionId as number,
					label: v.label || `#${v.dimensionId}`
				}));
		}
	});
}

export interface ReturnsListParams {
	skip?: number;
	take?: number;
	filters?: ReturnsFilters;
	/** Column key doubles as the backend selector — see `grid-row.ts`. */
	sort?: GridSort | null;
	/** Marks the request as an export, lifting the server's page-size clamp up to its export ceiling. */
	isExport?: boolean;
}

export interface ReturnsListResult {
	items: ReturnGridRow[];
	totalCount: number;
}

/**
 * The DevExtreme envelope for `returns/getall`.
 *
 * `filter` and `sort` are assigned as JSON **strings**: the generated Axios client flattens a real
 * array-of-objects to `sort.selector=…`, which `DataSourceLoadOptions` cannot read, while a string is
 * passed through verbatim and JSON-deserialised server-side (same contract as `lib/grid/sort.ts`).
 */
function buildLoadOptions({ skip = 0, take = 25, filters = {}, sort = null, isExport = false }: ReturnsListParams): DataSourceLoadOptions {
	const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
	const filter = buildReturnsFilter(filters);

	if (isExport) loadOptions.isExportRequest = 1;

	if (filter) (loadOptions as Record<string, unknown>).filter = JSON.stringify(filter);
	applySort(loadOptions, sort?.key ?? 'SubmittedAt', sort?.dir ?? 'desc');

	return loadOptions;
}

async function loadReturns(params: ReturnsListParams): Promise<ReturnsListResult> {
	const res = await returnsApi.getAll(buildLoadOptions(params));

	return { items: (res.data.data ?? []).map(toGridRow), totalCount: res.data.totalCount ?? 0 };
}

/**
 * Server-paged returns queue, one row per piece. `keepPreviousData` keeps the current page visible
 * while the next page or filter loads, so the grid doesn't flash empty (mirrors the reviews grid).
 */
export function useReturnsList(params: ReturnsListParams = {}) {
	const { skip = 0, take = 25, filters = {}, sort = null } = params;

	return useQuery<ReturnsListResult>({
		queryKey: returnKeys.list({ skip, take, filters, sort }),
		placeholderData: keepPreviousData,
		queryFn: () => loadReturns({ skip, take, filters, sort })
	});
}

/**
 * Fetches the filtered queue for a CSV export in one request, capped at `EXPORT_LIMIT` rows. The caller
 * is handed the unclamped `totalCount` so a truncated export can say so instead of passing silently.
 */
export async function fetchReturnsForExport(params: ReturnsListParams): Promise<{ rows: ReturnGridRow[]; totalCount: number }> {
	const { items, totalCount } = await loadReturns({ ...params, skip: 0, take: EXPORT_LIMIT, isExport: true });

	return { rows: items, totalCount };
}

/** The edit popup's payload. The only read surface that returns bank details. */
export function useReturnRequest(requestId: number | null) {
	return useQuery<ReturnRequestDetailToken>({
		queryKey: returnKeys.detail(requestId ?? 0),
		enabled: requestId != null,
		queryFn: async () => (await returnsApi.getById(requestId ?? 0)).data
	});
}

/**
 * Where a *selection* of pieces can be moved, and what stops the targets it cannot take.
 *
 * Asked of the server rather than derived from the selected rows: a row carries its status but not the
 * section-6 transition table (which the client never reimplements), and a piece whose request carries a
 * replacement basket never has its own `ReplacementDispatchedAt` stamped — judging the completion gate
 * here would refuse a replacement that has demonstrably been sent.
 *
 * Sorting the ids keeps the cache key stable — ticking A then B and B then A is one query, not two.
 * Query keys hash by value, so rebuilding the array on every render costs nothing.
 */
export function useReturnBulkStatusOptions(itemIds: readonly number[]) {
	const ids = [...itemIds].sort((a, b) => a - b);

	return useQuery<ReturnBulkStatusOptionToken[]>({
		queryKey: returnKeys.bulkStatusOptions(ids),
		enabled: ids.length > 0,
		queryFn: async () => (await returnsApi.bulkStatusOptions({ ids })).data.items ?? []
	});
}

/**
 * Whether the module is switched on for the active site.
 *
 * Read through `settings/getvalue`, which an `Orders`-role user can reach — unlike
 * `settings/getallbyparamgroup`, which needs `System`. A missing row means "off", matching the server
 * default, so a repository where nothing has been seeded simply reports the module as disabled.
 *
 * Only "no such setting" is swallowed. A 500 or a dropped connection is left to fail the query, because
 * rendering «the module is disabled» for five minutes over a transient error tells the administrator
 * something untrue and offers no way to retry.
 */
export function useReturnsEnabled() {
	return useQuery<boolean>({
		queryKey: returnKeys.enabled(),
		staleTime: 5 * 60_000,
		retry: false,
		queryFn: async () => {
			try {
				const res = await settingsApi.getValue(RETURNS_ENABLED_SETTING);
				return (
					String((res.data as unknown) ?? '')
						.trim()
						.toLowerCase() === 'true'
				);
			} catch (err) {
				if (isNotFound(err)) return false;
				throw err;
			}
		}
	});
}

/** A 404 from `settings/getvalue` is "never seeded", which is a real answer; anything else is a failure. */
const isNotFound = (err: unknown): boolean => (err as { response?: { status?: number } })?.response?.status === 404;

/**
 * The write surface of the queue. Every endpoint is deliberately granular — the core **records**,
 * it does not execute: nothing here moves money (Δ-10) or sends goods (Δ-11).
 */
export function useReturnMutations() {
	const qc = useQueryClient();

	// Deliberately not returned: `invalidateQueries` resolves only once every matching query has
	// refetched, and React Query awaits a promise returned from `onSuccess` before `mutateAsync` settles.
	// The popup saves up to five granular calls *per edited piece* in sequence, so returning it would make
	// every one of them block on a refetch of both the queue and the detail.
	const invalidate = () => {
		void qc.invalidateQueries({ queryKey: returnKeys.all });
	};

	/** Status, desired action and admin notes for a single piece. */
	const updateItem = useMutation({
		mutationFn: async ({ itemId, args }: { itemId: number; args: ReturnItemUpdateArgs }) => (await returnsApi.item(args, itemId)).data,
		onSuccess: invalidate
	});

	/**
	 * The same for a selection of pieces — a submission fans out into several and they are usually moved
	 * together. All-or-nothing server-side: one piece that cannot make the transition rolls the batch back,
	 * which is why the dialog only offers targets `useReturnBulkStatusOptions` reports as unblocked.
	 */
	const updateItems = useMutation({
		mutationFn: async (args: ReturnItemsUpdateArgs) => (await returnsApi.items(args)).data,
		onSuccess: invalidate
	});

	/** Allowed only while every piece is still "New request". */
	const removeRequest = useMutation({
		mutationFn: async (requestId: number) => (await returnsApi.remove(requestId)).data,
		onSuccess: invalidate
	});

	/** (Α-29) Records a refund the administrator has already executed outside the platform. */
	const recordRefund = useMutation({
		mutationFn: async ({ requestId, args }: { requestId: number; args: RefundExecutionArgs }) =>
			(await returnsApi.refundExecution(args, requestId)).data,
		onSuccess: invalidate
	});

	/** (Α-30) Records a replacement the administrator has already sent, per piece. */
	const recordDispatch = useMutation({
		mutationFn: async ({ requestId, args }: { requestId: number; args: ReplacementDispatchArgs }) =>
			(await returnsApi.replacementDispatch(args, requestId)).data,
		onSuccess: invalidate
	});

	/**
	 * (Α-30, Δ-11) Records the dispatch of ONE basket line — where the target lives when the request came from
	 * the customer (BW-7548). The Completed gate opens once every line is recorded.
	 */
	const recordLineDispatch = useMutation({
		mutationFn: async ({ requestId, args }: { requestId: number; args: ReplacementLineDispatchArgs }) =>
			(await returnsApi.replacementLineDispatch(args, requestId)).data,
		onSuccess: invalidate
	});

	/** (Α-31) Sets the replacement target of a piece; the server validates it against the catalogue. */
	const setReplacementTarget = useMutation({
		mutationFn: async ({ requestId, args }: { requestId: number; args: ReplacementSelectionArgs }) =>
			(await returnsApi.replacementSelection(args, requestId)).data,
		onSuccess: invalidate
	});

	return { updateItem, updateItems, removeRequest, recordRefund, recordDispatch, recordLineDispatch, setReplacementTarget };
}

/**
 * Opens a customer photo in a new tab.
 *
 * The content endpoint is authorised, so the bytes are fetched with the session's headers and handed
 * to the browser as an object URL — a plain anchor href would arrive unauthenticated, and the generic
 * anonymous `binaryrepofiles/get` would contradict §14.
 */
export async function openReturnPhoto(photoId: number): Promise<void> {
	const res = await returnsApi.photo(photoId, { responseType: 'blob' });
	const objectUrl = URL.createObjectURL(res.data as unknown as Blob);

	globalThis.open(objectUrl, '_blank', 'noopener,noreferrer');

	// The new tab keeps its own reference to the blob; revoking later frees ours without closing it.
	globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/** The returns policy of one catalog entity. Lives in its own table, so neither entity gains a field. */
export function useReturnPolicy(entityType: ReturnPolicyEntityType, entityId: number | undefined) {
	return useQuery<ReturnPolicyToken>({
		queryKey: returnKeys.policy(entityType, entityId ?? 0),
		enabled: Boolean(entityId),
		queryFn: async () => (await returnPolicyApi.get(entityType, entityId)).data
	});
}

export function useReturnPolicyMutation(entityType: ReturnPolicyEntityType, entityId: number | undefined) {
	const qc = useQueryClient();

	return useMutation({
		mutationFn: async (policy: ReturnPolicyToken) => (await returnPolicyApi.put(policy, entityType, entityId)).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: returnKeys.policy(entityType, entityId ?? 0) })
	});
}

/**
 * The module's configurable variables — the "Returns Settings" page.
 *
 * Not a filtered view of `settings/getall`: none of these rows ships in the database, so a plain query on
 * the Settings table would show an empty page on every fresh install. The server merges its own catalog with
 * whatever has been stored, which is why every row carries both `value` (stored, may be null) and
 * `effectiveValue` (what the module actually applies).
 */
export function useReturnSettings() {
	return useQuery<ReturnSettingToken[]>({
		queryKey: returnKeys.settings(),
		queryFn: async () => (await returnSettingsApi.getAll()).data.items ?? []
	});
}

/** Saves one variable. An empty value deletes the row and restores the default — see the server's `Put`. */
export function useReturnSettingMutation() {
	const qc = useQueryClient();

	return useMutation({
		mutationFn: async (args: ReturnSettingUpdateArgs) => (await returnSettingsApi.put(args)).data,
		// `Returns.Enabled` lives in this list, so the whole module's cache — including the enabled flag that
		// gates the queue page — is refreshed rather than just the settings query.
		onSuccess: () => qc.invalidateQueries({ queryKey: returnKeys.all })
	});
}
